import type { FollowType } from "@product/scmp-sdk";
import { FollowButtonAction, useFallbackStorageValue } from "@product/scmp-sdk";
import { useMountEffect } from "@react-hookz/web";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import useSWRImmutable from "swr/immutable";

import { useSnackbarSimple } from "~/components/common/snackbar/hooks";
import { config } from "~/data";
import { accountAtom } from "~/lib/account";
import { useDeferredAction } from "~/lib/hooks";
import { addMinutes, parseArray } from "~/lib/utils";

import { create, fetchList } from "./apis";
import { userFollowAtom } from "./atoms";
import { LastSyncStorageKey, UserFollowSwrFetchKey } from "./consts";
import { SnackbarMessageContainer, SnackbarViewAllLink } from "./styles";
import { generateNewList } from "./utils";

export const useUserFollowInit = () => {
  const { isLoggedIn } = useAtomValue(accountAtom);
  const [{ lastSync }, setUserFollowState] = useAtom(userFollowAtom);
  const { set: setLastSync, value: storedLastSync } = useFallbackStorageValue(LastSyncStorageKey, {
    defaultValue: 0,
  });

  useMountEffect(() => {
    setUserFollowState(current => ({ ...current, lastSync: storedLastSync ?? 0 }));
  });

  useEffect(() => {
    if (lastSync === undefined) return;
    setLastSync(lastSync);
  }, [lastSync, setLastSync]);

  const getIsDataOutDated = useCallback(
    () => Date.now() > addMinutes(lastSync ?? 0, 10).getTime(),
    [lastSync],
  );

  const { data, mutate } = useSWRImmutable(isLoggedIn ? UserFollowSwrFetchKey : null, fetchList, {
    isPaused: () => !getIsDataOutDated(),
    onSuccess: () => setUserFollowState(current => ({ ...current, lastSync: Date.now() })),
    refreshInterval: 1000 * 30,
    revalidateOnMount: true,
  });

  useEffect(() => {
    if (!data) return;
    setUserFollowState(current => ({ ...current, data }));
  }, [data, setUserFollowState]);

  useEffect(() => {
    if (!mutate) return;
    setUserFollowState(current => ({ ...current, mutate }));
  }, [mutate, setUserFollowState]);
};

export const useUserFollow = () => {
  const { isLoggedIn, user } = useAtomValue(accountAtom);
  const [{ data, mutate }, setUserFollowState] = useAtom(userFollowAtom);
  const { deferRun } = useDeferredAction([!!data, !!mutate]);
  const { handleOpenSnackbar } = useSnackbarSimple();

  const checkIsFollowed = useCallback(
    (type: FollowType, entityId: string) => !!data?.[`${type}s`].includes(entityId),
    [data],
  );

  const handleFollowAction = useCallback(
    (
      entityIds: string | string[],
      type: FollowType,
      action: FollowButtonAction,
      callback?: () => void,
    ) => {
      if (!isLoggedIn) return;
      deferRun(async () => {
        const payload = {
          action,
          ids: parseArray(entityIds),
          type,
          uuid: user?.uuid ?? "",
        };
        await mutate?.(data => create(data, payload), {
          optimisticData: data => generateNewList(data, payload),
          populateCache: true,
          revalidate: false,
          rollbackOnError: true,
        });
        setUserFollowState(current => ({ ...current, lastSync: Date.now() }));
        const message = action === FollowButtonAction.Follow ? "Following" : "Unfollowed";
        const snackbarParameter = {
          duration: 4000,
          leftElement: <SnackbarMessageContainer>{message}</SnackbarMessageContainer>,
          rightElement: (
            <SnackbarViewAllLink pathname={config.account.scmpAccountHost + "/manage/following"}>
              View all
            </SnackbarViewAllLink>
          ),
        };

        handleOpenSnackbar(snackbarParameter);
        callback?.();
      });
    },
    [deferRun, handleOpenSnackbar, isLoggedIn, mutate, setUserFollowState, user?.uuid],
  );

  return {
    checkIsFollowed,
    handleFollowAction,
  };
};
